import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { loadConfig } from '../api/data-qa-api';
import { ConfigSummary, LoadResult } from '../data-qa-types';

// Once the active model has loaded, warm the cache for every other discovered
// model in the background, ONE AT A TIME, so a later switch to them is instant
// too (matches useMmmDataQaLoad's queryKey/staleTime exactly, so this primes
// the same cache entries it reads from). Sequential on purpose — firing every
// other model's load concurrently competed with the active model (and with
// whatever the user does next) for Lambda/DB concurrency, which was a real
// regression, not a speed-up, on first load in a session when nothing is
// cached yet. `prefetchQuery` is a no-op for anything already cached and not
// stale, so this is safe to re-run on every render without re-fetching an
// already-warm model, and a manual switch mid-chain just reuses whichever
// prefetch is already in-flight or settled (React Query dedupes by queryKey)
// rather than double-fetching.
export const usePrefetchOtherModels = (
  cid: string | undefined,
  configs: ConfigSummary[],
  activeConfigPath: string | undefined,
  activeLoadResult: LoadResult | undefined
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!cid || !activeLoadResult) return undefined;
    let cancelled = false;

    const prefetchRemainingModels = async () => {
      for (const config of configs) {
        if (cancelled) return;
        if (config.configPath === activeConfigPath) continue;
        await queryClient.prefetchQuery({
          queryKey: ['mmmDataQALoad', cid, config.configPath],
          queryFn: () => loadConfig(cid, config.configPath),
          staleTime: Infinity
        });
      }
    };
    prefetchRemainingModels();

    return () => {
      cancelled = true;
    };
  }, [cid, configs, activeConfigPath, activeLoadResult, queryClient]);
};
